import { BoundaryParseError } from './errors'
import type { ZodLikeSchema } from './zod-like-schema'

/**
 * The core boundary primitive: take an `unknown` value, run it through a
 * schema, return the typed value or throw `BoundaryParseError`. Every other
 * boundary helper in this package wraps `parseAtBoundary` after fetching raw
 * data from its source — fetch from network, fetch from storage, fetch from
 * a cookie, all collapse into "now parse what you got".
 *
 * Pure function — no IO. Tested directly. Other boundary primitives are thin
 * IO wrappers around this.
 */
export function parseAtBoundary<T>(
  boundaryName: string,
  schema: ZodLikeSchema<T>,
  raw: unknown,
): T {
  const result = schema.safeParse(raw)
  if (!result.success) {
    throw new BoundaryParseError(
      boundaryName,
      `[trust-boundary] "${boundaryName}" received data that does not match its schema. The source is lying about its contract — fix the source or the schema, do not coerce.`,
      result.error,
      raw,
    )
  }
  // safeParse contract: success=true ⇒ data is set. Cast is local and audited.
  return result.data as T
}
